export type SyncTerminalStatus = 'completed' | 'failed' | 'cancelled';

export interface SyncStreamHandle { close(): void }

export interface OpenSyncRunStreamOptions<T> {
  /** A status returned by the initial status lookup, when one is available. */
  status?: string | null;
  url: string;
  fetchStatus: () => Promise<T>;
  statusOf: (value: T) => string | null | undefined;
  onEvent: (event: MessageEvent) => void;
  onStatus: (value: T) => void;
  onTerminal?: (status: SyncTerminalStatus) => void;
  eventSource?: (url: string) => EventSource;
}

export const isTerminalSyncStatus = (status?: string | null): status is SyncTerminalStatus =>
  status === 'completed' || status === 'failed' || status === 'cancelled';

/** While a stream is open, also check status on this cadence so a missed terminal event can never freeze the UI. */
export const SYNC_STATUS_SAFETY_POLL_MS = 12_000;

/**
 * Opens the sync stream only while a run can still make progress. A broken stream
 * gets one immediate status check, then retries at 8s, 16s, and 30s thereafter.
 */
export function openSyncRunStream<T>(options: OpenSyncRunStreamOptions<T>): SyncStreamHandle {
  let source: EventSource | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let closed = false;
  let terminalEventReceived = isTerminalSyncStatus(options.status);
  let attempt = 0;
  let checkingStatus = false;
  let safety: ReturnType<typeof setInterval> | null = null;
  const stopSafety = () => {
    if (safety !== null) clearInterval(safety);
    safety = null;
  };

  const close = () => {
    closed = true;
    stopSafety();
    source?.close();
    source = null;
    if (timer !== null) clearTimeout(timer);
    timer = null;
  };

  const checkStatus = async (): Promise<boolean> => {
    if (closed || terminalEventReceived) return true;
    if (checkingStatus) return false;
    checkingStatus = true;
    try {
      const result = await options.fetchStatus();
      if (closed || terminalEventReceived) return true;
      options.onStatus(result);
      const status = options.statusOf(result);
      if (isTerminalSyncStatus(status)) {
        terminalEventReceived = true;
        options.onTerminal?.(status);
        close();
        return true;
      }
    } catch (error) {
      // A failed SSE handshake can poison concurrent requests. Keep this quiet
      // for subscribers and leave a diagnostic for developers only.
      console.debug('[sync] status check after stream failure failed', error);
    } finally {
      checkingStatus = false;
    }
    return closed;
  };

  const schedulePoll = () => {
    if (closed) return;
    const delay = Math.min(8_000 * 2 ** attempt, 30_000);
    attempt += 1;
    timer = setTimeout(async () => {
      timer = null;
      if (await checkStatus()) return;
      schedulePoll();
    }, delay);
  };

  if (terminalEventReceived) return { close };

  source = (options.eventSource ?? ((url: string) => new EventSource(url)))(options.url);
  const eventNames = ['progress', 'review_required', 'audit_pending', 'complete', 'error', 'cancelled'];
  const handleEvent = (event: MessageEvent) => {
    options.onEvent(event);
    try {
      const data = JSON.parse(event.data) as { status?: string };
      if (isTerminalSyncStatus(data.status)) {
        terminalEventReceived = true;
        close();
        options.onTerminal?.(data.status);
      }
    } catch { /* event consumers may accept non-JSON payloads */ }
  };
  eventNames.forEach((name: string) => source?.addEventListener(name, handleEvent));
  // Safety net: a healthy-looking stream that never delivers the terminal event
  // (seen live 2026-10-02: page frozen at 7/14 after the run had ended) is caught here.
  safety = setInterval(() => {
    if (closed || terminalEventReceived) { stopSafety(); return; }
    void checkStatus();
  }, SYNC_STATUS_SAFETY_POLL_MS);
  source.onerror = () => {
    stopSafety();
    source?.close();
    source = null;
    if (closed || terminalEventReceived) return;
    console.debug('[sync] stream failed; checking status immediately before polling');
    void checkStatus().then((done: boolean) => {
      if (!done) schedulePoll();
    });
  };

  return { close };
}

export interface FailedPhase { status?: string; error?: string }

/** Internal failure codes mapped to subscriber copy. Never show codes, UUIDs or provider URLs. */
const FRIENDLY_PHASE_ERRORS: Array<[RegExp, string]> = [
  [/arch_review_repo_snapshot_unavailable|local snapshot unavailable/i, "We couldn't download your repository for this step. Run Sync again; if it repeats, contact support."],
  [/semgrep|configured scan paths/i, "The code security scan couldn't run on this repository. Run Sync again; if it repeats, contact support."],
  [/timed out|timeout/i, 'This step took too long and was stopped. Run Sync again.'],
];

export function failedPhaseMessage(phases?: FailedPhase[] | null): string | null {
  const error = phases?.find((phase: FailedPhase) => phase.status === 'failed')?.error;
  if (!error) return null;
  const friendly = FRIENDLY_PHASE_ERRORS.find(([pattern]) => pattern.test(error));
  if (friendly) return friendly[1];
  return error
    .replace(/https:\/\/docs\.github\.com\S*/g, '')
    .replace(/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi, '')
    .replace(/\s*-\s*$/g, '')
    .trim() || null;
}
